import React, { useCallback, useContext, useMemo, useState } from 'react'
import { RegionDataTransport, RegionMapTransport } from '../../../transports/home-page-transport'
import { Container, ColumnBox, SliderContainer } from './region-count-styles'
import { ScreenContext } from 'react-components'
import { useTranslation } from '../../../hooks/locale'
import { RegionItemCard } from './region-item-card'
import { TabButton, TabsContainer } from '../common/styles'
import { EmblaCarousel } from '../../common/embla-carousel'

enum RegionTabs {
    EUROPE = 'europe',
    STATE = 'state',
}

const VISIBLE_SLIDES_CONFIG = {
    mobile: 1.2,
    tablet: 2.5,
    desktop: 4,
} as const

interface Props {
    regionsMap: RegionMapTransport
    itemsPerCol?: number
    keyPrefix: string
}

export const RegionCountMap = React.memo(function RegionCountMap({ regionsMap, itemsPerCol = 7, keyPrefix }: Props) {
    const { t } = useTranslation()
    const { isTablet, isDesktop } = useContext(ScreenContext)
    const [activeTab, setActiveTab] = useState<RegionTabs>(RegionTabs.EUROPE)

    const visibleSlides = isTablet
        ? VISIBLE_SLIDES_CONFIG.tablet
        : isDesktop
          ? VISIBLE_SLIDES_CONFIG.desktop
          : VISIBLE_SLIDES_CONFIG.mobile

    const tabsData = useMemo(
        () => [
            {
                type: RegionTabs.EUROPE,
                regions: regionsMap.europe,
                key: 'europe',
            },
            {
                type: RegionTabs.STATE,
                regions: regionsMap.state,
                key: 'state',
            },
        ],
        [regionsMap],
    )

    const getColumnData = useCallback(
        (regions: RegionDataTransport[]) => {
            const colCount = Math.max(1, Math.ceil(regions.length / itemsPerCol))
            return Array.from({ length: colCount }, (_, i) => regions.slice(i * itemsPerCol, (i + 1) * itemsPerCol))
        },
        [itemsPerCol],
    )

    const renderColumn = (column: RegionDataTransport[], colIndex: number, tabType: string) => (
        <ColumnBox key={`${tabType}-col-${colIndex}`}>
            {column.map(region => (
                <RegionItemCard key={`${tabType}-${region.title}`} region={region} prefix={keyPrefix} />
            ))}
        </ColumnBox>
    )

    const handleTabEurope = useCallback(() => setActiveTab(RegionTabs.EUROPE), [])
    const handleTabState = useCallback(() => setActiveTab(RegionTabs.STATE), [])

    return (
        <Container>
            <TabsContainer>
                <TabButton active={activeTab === RegionTabs.EUROPE} onClick={handleTabEurope}>
                    {t('europe')}
                </TabButton>
                <TabButton active={activeTab === RegionTabs.STATE} onClick={handleTabState}>
                    {t('inFederalState')}
                </TabButton>
            </TabsContainer>
            {tabsData.map(tab => {
                const columnData = getColumnData(tab.regions)
                const isActive = activeTab === tab.type
                return (
                    <SliderContainer key={tab.key} $isVisible={isActive}>
                        <EmblaCarousel
                            slides={columnData.map((column, colIndex) => renderColumn(column, colIndex, tab.key))}
                            visibleSlides={visibleSlides}
                            slidesToScroll={1}
                            showNav
                            navVariant="top-right"
                            navPosition={{
                                desktopTop: '-6rem',
                                desktopRight: '0rem',
                                wideTop: '-6.15rem',
                                wideRight: '0rem',
                            }}
                        />
                    </SliderContainer>
                )
            })}
        </Container>
    )
})
